import {
  NookDatabase,
  assertWritable,
  markSearchDirty,
} from "../infrastructure/database/database";
import { assertCapacity, sha256 } from "../infrastructure/backup/snapshot";
import { collectAttachments } from "./attachment-gc";
import {
  sopFieldsSchema,
  sopDraftSchema,
  sopTemplateSchema,
  sopVersionSchema,
  sopRunSchema,
  sopResultSchema,
  assertRunBinding,
  type SopFields,
  type SopRun,
  type SopTemplate,
  type SopRevision,
  type SopResult,
} from "../domain/sop/model";
export class SopService {
  constructor(readonly db: NookDatabase) {}
  private async command<T>(
    op: string,
    input: unknown,
    run: () => Promise<{ value: T; id: string; revision: number }>,
    duplicate: (id: string) => Promise<T>,
  ) {
    const fingerprint = await sha256(new Blob([JSON.stringify(input)]));
    return this.db.transaction("rw", this.db.tables, async () => {
      await assertWritable(this.db);
      const old = await this.db.operations.get(op);
      if (old) {
        if (old.kind !== "sop-command" || old.fingerprint !== fingerprint)
          throw Error("操作标识已用于不同输入");
        return duplicate(old.resultId);
      }
      const r = await run();
      await this.db.operations.add({
        id: op,
        kind: "sop-command",
        fingerprint,
        resultId: r.id,
        resultRevision: r.revision,
        committedAt: new Date().toISOString(),
      });
      await markSearchDirty(this.db);
      await assertCapacity(this.db);
      return r.value;
    });
  }
  private async history(
    kind: "sop-template" | "sop-run",
    item: SopTemplate | SopRun,
    reason: SopRevision["reason"],
  ) {
    await this.db.revisions.add({
      id: `${kind}:${item.id}:${item.revision}`,
      entityType: kind,
      entityId: item.id,
      revision: item.revision,
      reason,
      recordedAt: item.updatedAt,
      snapshot: item,
    });
  }
  private async uniqueId(id: string) {
    for (const name of [
      "captures",
      "tasks",
      "events",
      "projects",
      "resources",
      "decisions",
      "diaryEntries",
      "sopTemplates",
      "sopRuns",
      "manualStudyPlans",
      "manualStudySessions",
    ])
      if (await this.db.table(name).get(id)) throw Error("对象标识已存在");
  }
  async saveDraft(
    targetId: string,
    baseRevision: number,
    payload: SopFields,
    id: string,
    expected: number,
  ) {
    return this.command(
      crypto.randomUUID(),
      { targetId, baseRevision, payload, id, expected },
      async () => {
        const old = await this.db.sopDrafts.get(id);
        if (old && old.targetId !== targetId) throw Error("草稿归属不同");
        if (baseRevision) {
          const target = await this.db.sopTemplates.get(targetId);
          if (!target || baseRevision > target.revision)
            throw Error("原模板不存在或草稿基线来自未来");
        }
        const fork = !!old?.deletedAt || (old?.revision ?? 0) !== expected,
          now = new Date().toISOString();
        const d = sopDraftSchema.parse({
          id: fork ? crypto.randomUUID() : id,
          targetId,
          baseRevision,
          revision: fork ? 1 : expected + 1,
          createdAt: fork ? now : (old?.createdAt ?? now),
          updatedAt: now,
          deletedAt: null,
          payload,
        });
        await this.db.sopDrafts.put(d);
        return { value: d, id: d.id, revision: d.revision };
      },
      async (id) => (await this.db.sopDrafts.get(id))!,
    );
  }
  async publish(draftId: string, expected: number, op: string, asCopy = false) {
    return this.command(
      op,
      { draftId, expected, asCopy },
      async () => {
        const d = await this.db.sopDrafts.get(draftId);
        if (!d || d.deletedAt || d.revision !== expected)
          throw Error("草稿已改变，未覆盖");
        const old = await this.db.sopTemplates.get(d.targetId);
        if (
          !asCopy &&
          (old?.deletedAt || (old?.revision ?? 0) !== d.baseRevision)
        )
          throw Error("模板已更新；草稿保留，可另存模板");
        const fields = sopFieldsSchema.parse(d.payload),
          now = new Date().toISOString(),
          id = asCopy ? crypto.randomUUID() : d.targetId;
        if (!old || asCopy) await this.uniqueId(id);
        const versions = asCopy
          ? []
          : await this.db.sopTemplateVersions
              .where("templateId")
              .equals(id)
              .toArray();
        const v = sopVersionSchema.parse({
          id: crypto.randomUUID(),
          templateId: id,
          version: Math.max(0, ...versions.map((v) => v.version)) + 1,
          createdAt: now,
          ...fields,
          attachmentIds: asCopy ? [] : (old?.attachmentIds ?? []),
        });
        const t = sopTemplateSchema.parse({
          id,
          createdAt: !asCopy && old ? old.createdAt : now,
          updatedAt: now,
          revision: !asCopy && old ? old.revision + 1 : 1,
          source: "user",
          archivedAt: !asCopy ? (old?.archivedAt ?? null) : null,
          deletedAt: null,
          attachmentIds: !asCopy ? (old?.attachmentIds ?? []) : [],
          pinned: !asCopy ? (old?.pinned ?? false) : false,
          currentVersionId: v.id,
        });
        await this.db.sopTemplateVersions.add(v);
        await this.db.sopTemplates.put(t);
        await this.history(
          "sop-template",
          t,
          !asCopy && old ? "edited" : "created",
        );
        await this.db.sopDrafts.delete(d.id);
        return { value: t, id: t.id, revision: t.revision };
      },
      async (id) => {
        const t = await this.db.sopTemplates.get(id);
        if (!t) throw Error("已提交模板已移除");
        return t;
      },
    );
  }
  async start(
    templateId: string,
    versionId: string,
    expected: number,
    op: string,
  ) {
    return this.command(
      op,
      { templateId, versionId, expected },
      async () => {
        const t = await this.db.sopTemplates.get(templateId),
          v = await this.db.sopTemplateVersions.get(versionId);
        if (
          !t ||
          t.deletedAt ||
          t.revision !== expected ||
          t.currentVersionId !== versionId ||
          !v ||
          v.templateId !== templateId
        )
          throw Error("模板已改变，请查看版本后再开始");
        const now = new Date().toISOString(),
          r = sopRunSchema.parse({
            id: crypto.randomUUID(),
            templateId,
            templateVersionId: v.id,
            state: "active",
            cursor: 0,
            results: v.steps.map((s) => ({
              stepId: s.id,
              status: "pending",
              note: "",
              reason: "",
            })),
            outcomeReason: "",
            endedAt: null,
            createdAt: now,
            updatedAt: now,
            revision: 1,
            source: "user",
            archivedAt: null,
            deletedAt: null,
            attachmentIds: [],
          });
        await this.db.sopRuns.add(r);
        await this.history("sop-run", r, "created");
        return { value: r, id: r.id, revision: r.revision };
      },
      async (id) => {
        const r = await this.db.sopRuns.get(id);
        if (!r) throw Error("原执行已移除");
        return r;
      },
    );
  }
  async updateRun(
    id: string,
    expected: number,
    change:
      | { action: "step"; result: SopResult; advance?: boolean }
      | { action: "cursor"; cursor: number }
      | {
          action: "pause" | "resume" | "completed" | "partial" | "cancelled";
          reason?: string;
        },
    op: string,
  ) {
    return this.command(
      op,
      { id, expected, change },
      async () => {
        const old = await this.db.sopRuns.get(id);
        if (!old || old.deletedAt || old.revision !== expected)
          throw Error("执行已改变；未覆盖本次输入");
        const version = await this.db.sopTemplateVersions.get(
          old.templateVersionId,
        );
        if (!version) throw Error("执行原版本缺失");
        assertRunBinding(old, version);
        let r = {
          ...old,
          results: old.results.map((x) => ({ ...x })),
          revision: old.revision + 1,
          updatedAt: new Date().toISOString(),
        };
        let reason: SopRevision["reason"] = "edited";
        if (change.action === "resume") {
          if (old.state !== "paused") throw Error("仅暂停的执行可继续");
          r.state = "active";
          reason = "resumed";
        } else {
          if (old.state !== "active")
            throw Error("请先继续执行；已结束执行不能更改步骤");
          if (change.action === "step") {
            const result = sopResultSchema.parse(change.result),
              index = version.steps.findIndex((s) => s.id === result.stepId);
            if (index < 0) throw Error("步骤不属于该执行版本");
            r.results[index] = result;
            if (change.advance)
              r.cursor = index < version.steps.length - 1 ? index + 1 : index;
          } else if (change.action === "cursor") {
            if (change.cursor < 0 || change.cursor >= version.steps.length)
              throw Error("当前位置越界");
            r.cursor = change.cursor;
            reason = "cursor";
          } else if (change.action === "pause") {
            r.state = "paused";
            reason = "paused";
          } else {
            r.state = change.action;
            r.endedAt = r.updatedAt;
            r.outcomeReason = change.reason ?? "";
            reason = change.action;
          }
        }
        r = sopRunSchema.parse(r);
        assertRunBinding(r, version);
        await this.db.sopRuns.put(r);
        await this.history("sop-run", r, reason);
        return { value: r, id: r.id, revision: r.revision };
      },
      async (id) => (await this.db.sopRuns.get(id))!,
    );
  }
  async templateHistoryRestore(
    id: string,
    revision: number,
    historyId: string,
    op: string,
  ) {
    return this.command(
      op,
      { id, revision, historyId },
      async () => {
        const t = await this.db.sopTemplates.get(id),
          h = await this.db.revisions.get(historyId);
        if (
          !t ||
          t.deletedAt ||
          t.revision !== revision ||
          !h ||
          !("entityType" in h) ||
          h.entityType !== "sop-template" ||
          h.entityId !== id ||
          !("currentVersionId" in h.snapshot)
        )
          throw Error("历史恢复边界无效");
        const v = await this.db.sopTemplateVersions.get(
          h.snapshot.currentVersionId,
        );
        if (!v) throw Error("历史模板版本缺失");
        const all = await this.db.sopTemplateVersions
            .where("templateId")
            .equals(id)
            .toArray(),
          now = new Date().toISOString(),
          copy = {
            ...v,
            id: crypto.randomUUID(),
            version: Math.max(...all.map((v) => v.version)) + 1,
            createdAt: now,
          };
        await this.db.sopTemplateVersions.add(copy);
        const current = {
          ...t,
          currentVersionId: copy.id,
          updatedAt: now,
          revision: t.revision + 1,
        };
        await this.db.sopTemplates.put(current);
        await this.history("sop-template", current, "history-restored");
        return { value: current, id, revision: current.revision };
      },
      async (id) => (await this.db.sopTemplates.get(id))!,
    );
  }
  async lifecycle(
    kind: "sop-template" | "sop-run" | "sop-draft",
    id: string,
    expected: number,
    action:
      | "archive"
      | "unarchive"
      | "trash"
      | "restore"
      | "purge"
      | "pin"
      | "unpin",
    op: string,
  ) {
    return this.command(
      op,
      { kind, id, expected, action },
      async () => {
        const table =
          kind === "sop-template"
            ? this.db.sopTemplates
            : kind === "sop-run"
              ? this.db.sopRuns
              : this.db.sopDrafts;
        const old = await table.get(id);
        if (!old || old.revision !== expected) throw Error("对象已改变");
        if (action === "purge") {
          if (!old.deletedAt) throw Error("仅回收站对象可永久删除");
          if (kind === "sop-template") {
            if (
              (await this.db.sopRuns.where("templateId").equals(id).count()) ||
              (await this.db.sopDrafts.where("targetId").equals(id).count())
            )
              throw Error("模板仍被执行或草稿引用（含回收站）；先处理这些对象");
            await this.db.sopTemplateVersions
              .where("templateId")
              .equals(id)
              .delete();
          }
          await table.delete(id);
          if (kind !== "sop-draft")
            await this.db.revisions
              .where("[entityType+entityId]")
              .equals([kind, id])
              .delete();
          await collectAttachments(this.db);
          return { value: undefined, id, revision: expected };
        }
        if (kind === "sop-draft" && !["trash", "restore"].includes(action))
          throw Error("草稿仅支持回收或恢复");
        if (
          (action === "restore" && !old.deletedAt) ||
          (action !== "restore" && old.deletedAt)
        )
          throw Error("请先恢复对象");
        const now = new Date().toISOString(),
          x = { ...old, revision: old.revision + 1, updatedAt: now };
        if (action === "trash" || action === "restore")
          x.deletedAt = action === "trash" ? now : null;
        else if (
          "archivedAt" in x &&
          (action === "archive" || action === "unarchive")
        )
          x.archivedAt = action === "archive" ? now : null;
        else if ("pinned" in x && (action === "pin" || action === "unpin"))
          x.pinned = action === "pin";
        else throw Error("操作不适用于此对象");
        await (table as import("dexie").Table).put(x);
        if (kind !== "sop-draft")
          await this.history(
            kind,
            x as SopTemplate | SopRun,
            action === "trash"
              ? "trashed"
              : action === "restore"
                ? "restored"
                : "edited",
          );
        return { value: undefined, id, revision: x.revision };
      },
      async () => undefined,
    );
  }
}
